import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import KaTeXContent from '../common/KaTeXContent'; // Assuming this path is correct
import { truncateTaskTitle } from '../../utils/formatters';
import {
  cleanSvgMarkup,
  hasDiagram,
  normalizeDiagramType,
  normalizeSvgForDisplay,
  requestTikzToSvg,
} from '../../utils/diagramEditor';
import useDiagramEditor from '../../hooks/useDiagramEditor';
import DiagramEditorHost from '../worksheet/DiagramEditorHost';
import EmojiDiagramView from '../worksheet/EmojiDiagramView';
import {
  cacheSvg,
  getCachedSvg,
  postSvgCacheToServer,
  subscribeCacheUpdate,
} from '../../utils/tikzSvgCache';
import { useAuth } from '../../contexts/AuthContext';
import {
  canUserSeeQuestionDifficulty,
  DifficultyBadge,
} from '../worksheet/questionDifficulty';

const ASSERTION_REASONING_OPTIONS = [
  'Both A and R are true, and R is the correct explanation of A',
  'Both A and R are true, but R is NOT the correct explanation of A',
  'A is true, but R is false',
  'A is false, but R is true',
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripAssertionReasoningOptions = (text) => {
  if (!text) return text;

  const normalizedText = text.toLowerCase();
  const matchCount = ASSERTION_REASONING_OPTIONS.filter((option) =>
    normalizedText.includes(option.toLowerCase()),
  ).length;

  if (matchCount < 2) {
    return text;
  }

  let cleaned = text;

  ASSERTION_REASONING_OPTIONS.forEach((option) => {
    const escapedOption = escapeRegExp(option);
    cleaned = cleaned.replace(
      new RegExp(
        `<li[^>]*>\\s*(?:[A-Da-d][\\).\\:]\\s*)?${escapedOption}\\s*<\\/li>`,
        'gi',
      ),
      '',
    );
    cleaned = cleaned.replace(
      new RegExp(
        `(?:^|<br\\s*/?>|\\n|\\r)\\s*(?:[A-Da-d][\\).\\:]\\s*)?${escapedOption}\\s*(?=(<br\\s*/?>|\\n|\\r|$))`,
        'gi',
      ),
      '$1',
    );
    cleaned = cleaned.replace(
      new RegExp(`\\s*\\([A-Da-d]\\)\\s*${escapedOption}`, 'gi'),
      '',
    );
  });

  cleaned = cleaned.replace(/<ol[^>]*>\s*<\/ol>/gi, '');
  cleaned = cleaned.replace(/<ul[^>]*>\s*<\/ul>/gi, '');

  return cleaned.trim();
};

// Memoized Question Content to prevent KaTeX re-renders when answers change
const QuestionContent = React.memo(({ questionNumber, questionText, marks, diagram, DiagramRenderer, worksheetId, difficulty, showDifficulty }) => {
  return (
    <>
      {/* Question Number Badge + difficulty (parent/teacher only) */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
          Question {questionNumber}
          {marks && ` • ${marks} ${marks === 1 ? 'mark' : 'marks'}`}
        </div>
        {showDifficulty && <DifficultyBadge difficulty={difficulty} />}
      </div>

      {/* Question Text with KaTeX/TikZ support */}
      <div className="text-lg text-gray-900 mb-4">
        <KaTeXContent 
          key={`question-${questionNumber}`}
          content={questionText} 
        />
      </div>

      {/* Diagram if present with KaTeX/TikZ/SVG support */}
      {diagram && (diagram.svg_cache || diagram.code) && (
        <div className="my-4 p-4 bg-gray-50 rounded-lg">
          <DiagramRenderer
            diagramCode={diagram.svg_cache || diagram.code}
            fromSvgCache={Boolean(diagram.svg_cache)}
            declaredType={normalizeDiagramType(diagram.type)}
            worksheetId={worksheetId}
          />
        </div>
      )}

      {/* Handle "emoji" diagrams separately */}
      {diagram && normalizeDiagramType(diagram.type) === 'emoji' && !diagram.code && (
        <div className="my-4 p-4 bg-gray-50 rounded-lg">
          <EmojiDiagramView diagram={diagram} />
        </div>
      )}
    </>
  );
});

QuestionContent.displayName = 'QuestionContent';

/**
 * DiagramRenderer Component
 * Renders TikZ diagrams in a self-contained, isolated iframe.
 * Renders SVG directly.
 * This version uses text-align for centering and allows the iframe
 * element itself to scroll, fixing cropping.
 */
const DIAGRAM_TYPE_BADGES = {
  tikz: { text: 'TikZ', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  svg: { text: 'SVG', cls: 'bg-green-50 text-green-700 border-green-200' },
  svgRendered: { text: 'SVG (rendered)', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  svgCached: { text: 'SVG (cached)', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const DiagramTypeBadge = ({ kind }) => {
  const badge = DIAGRAM_TYPE_BADGES[kind];
  if (!badge) return null;
  return (
    <div className="mb-1.5">
      <span
        className={`inline-block px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded border ${badge.cls}`}
      >
        {badge.text}
      </span>
    </div>
  );
};

function DiagramRenderer({ diagramCode, className = "", fromSvgCache = false, declaredType = '', worksheetId = '' }) {

  const normalizedDiagramCode = useMemo(() => {
    if (typeof diagramCode === 'string') {
      return diagramCode;
    }
    if (diagramCode == null) {
      return '';
    }
    if (typeof diagramCode === 'object') {
      try {
        return JSON.stringify(diagramCode);
      } catch (_error) {
        return String(diagramCode);
      }
    }
    return String(diagramCode);
  }, [diagramCode]);

  // Detect SVG on the *raw* code: a cached SVG from the TikZ sidecar can contain
  // valid Unicode in <text> nodes (Greek letters, math/currency symbols, etc.).
  // The ASCII strip below would silently mangle those into garbage HTML and
  // bubble up as a render error — keep SVG content verbatim.
  const isSVG = useMemo(() => {
    if (!normalizedDiagramCode) return false;
    // svg_cache always stores rendered SVG markup. Trust the explicit signal
    // from the parent: the backend's sidecar can produce SVG that starts with
    // a comment or processing instruction the regex below would miss, which
    // would otherwise drop the content into the TikZ rendering branch and
    // surface a misleading "TikZ" badge.
    if (fromSvgCache) return true;
    // Trust an explicit SVG declared type (covers AI variants like "inline_svg"
    // already normalized upstream) — the regex below can miss SVG payloads
    // that omit the xmlns attribute.
    if (declaredType === 'svg') return true;
    return /<\?xml|<svg[\s>]|xmlns="http:\/\/www\.w3\.org\/2000\/svg"/i.test(normalizedDiagramCode);
  }, [normalizedDiagramCode, fromSvgCache, declaredType]);

  const sanitizedDiagramCode = useMemo(() => {
    if (!normalizedDiagramCode) {
      return '';
    }

    if (isSVG) {
      // Strip backslash-escaped quotes the AI sometimes emits inside SVG
      // markup; without this the browser sees attributes like xmlns=\"..\"
      // and fails to parse the element, leaving a blank diagram box.
      return cleanSvgMarkup(normalizedDiagramCode);
    }
    // TikZJax (and the bundled TeX engine) can fail on unsupported unicode
    // glyphs (e.g., ₹). Keep ASCII/newline input for TikZ source stability.
    return normalizedDiagramCode.replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
  }, [normalizedDiagramCode, isSVG]);

  // Re-check the cache when the background prerender lands a new entry
  // for this diagram — without this hook the useMemo below would only run
  // on diagram-code changes and miss the cache write.
  const [cacheTick, setCacheTick] = useState(0);
  useEffect(() => {
    if (!sanitizedDiagramCode || isSVG) return undefined;
    if (!sanitizedDiagramCode.includes('\\begin{tikzpicture}')) return undefined;
    const unsubscribe = subscribeCacheUpdate((updatedCode) => {
      if (updatedCode !== sanitizedDiagramCode) return;
      setCacheTick((tick) => tick + 1);
    });
    return unsubscribe;
  }, [sanitizedDiagramCode, isSVG]);

  // If a previous render already produced an SVG for this TikZ source, reuse
  // it instead of re-running TikZJax. This is what makes the worksheet show
  // "SVG only" after the diagram has been rendered once in any view.
  const cachedSvgForTikz = useMemo(() => {
    if (isSVG || !sanitizedDiagramCode) return '';
    if (!sanitizedDiagramCode.includes('\\begin{tikzpicture}')) return '';
    return getCachedSvg(sanitizedDiagramCode) || '';
  }, [isSVG, sanitizedDiagramCode, cacheTick]);

  const isTikZ = !isSVG && !cachedSvgForTikz && sanitizedDiagramCode.includes('\\begin{tikzpicture}');

  // SVG-only display: when the diagram is TikZ and we don't have an SVG
  // for it yet, ask the server's sidecar to render one. This is plain
  // HTTP — no offscreen iframe, no TikZJax WebAssembly, no extra heap
  // pressure inside the question view. When the server succeeds we
  // cache the SVG locally; subscribeCacheUpdate then bumps cacheTick
  // above and the next render hits the `cachedSvgForTikz` branch with
  // the inline SVG. Failures show a stable "diagram unavailable"
  // placeholder rather than spinning forever.
  const [serverRenderFailed, setServerRenderFailed] = useState(false);
  useEffect(() => {
    setServerRenderFailed(false);
    if (!isTikZ) return undefined;
    if (!sanitizedDiagramCode) return undefined;
    let cancelled = false;
    requestTikzToSvg(sanitizedDiagramCode)
      .then((svg) => {
        if (cancelled) return;
        if (!svg) {
          setServerRenderFailed(true);
          return;
        }
        const normalized = normalizeSvgForDisplay(svg) || svg;
        cacheSvg(sanitizedDiagramCode, normalized);
        if (worksheetId) {
          postSvgCacheToServer(worksheetId, [
            { tikzCode: sanitizedDiagramCode, svgHtml: normalized },
          ]);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setServerRenderFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isTikZ, sanitizedDiagramCode, worksheetId]);

  // Pick the badge that matches what's actually being rendered below.
  let badgeKind = null;
  if (cachedSvgForTikz) {
    badgeKind = 'svgCached';
  } else if (isSVG && (fromSvgCache || (declaredType && declaredType !== 'svg'))) {
    badgeKind = 'svgRendered';
  } else if (isSVG) {
    badgeKind = 'svg';
  } else if (isTikZ) {
    badgeKind = 'tikz';
  }
  const badge = <DiagramTypeBadge kind={badgeKind} />;

  // --- Render Logic ---
  if (cachedSvgForTikz) {
    return (
      <>
        {badge}
        <div className={"min-h-[120px] w-full overflow-auto rounded-lg border p-3 " + className}>
          <div
            className="diagram-svg-box mx-auto max-w-full"
            dangerouslySetInnerHTML={{ __html: cachedSvgForTikz }}
          />
        </div>
      </>
    );
  }

  if (isTikZ) {
    // SVG-only display: never compile TikZ inside the question view.
    // The earlier iframe-based renderer (DiagramTile wrapped in
    // DiagramViewerButton) booted a fresh TikZJax WebAssembly instance
    // on click — on Safari and mobile WebViews repeated mount/unmount
    // cycles exhausted memory and reloaded the tab. While we wait for
    // the server (or background prerender) to produce the SVG we show a
    // stable placeholder; subscribeCacheUpdate flips us to inline SVG
    // the moment it arrives.
    const placeholderText = serverRenderFailed
      ? 'Diagram unavailable'
      : 'Rendering diagram…';
    const placeholderIcon = serverRenderFailed ? (
      <span aria-hidden="true">⚠</span>
    ) : (
      <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
    );
    const placeholderTone = serverRenderFailed
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-blue-50 border-blue-200 text-blue-700';
    return (
      <>
        {badge}
        <div
          className={`min-h-[120px] w-full overflow-auto rounded-lg border p-3 flex items-center justify-center ${className}`}
          role={serverRenderFailed ? 'alert' : 'status'}
          aria-live="polite"
        >
          <div
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${placeholderTone}`}
          >
            {placeholderIcon}
            <span>{placeholderText}</span>
          </div>
        </div>
      </>
    );
  }
  
  if (isSVG) {
    // Render SVG directly, adding flex styles for centering
    return (
      <>
        {badge}
        <div className={"min-h-[120px] w-full overflow-auto rounded-lg border p-3 " + className}>
          <div
            className="diagram-svg-box mx-auto max-w-full"
            dangerouslySetInnerHTML={{ __html: sanitizedDiagramCode }}
          />
        </div>
      </>
    );
  }
  
  // Fallback for non-TikZ/non-SVG code
  return (
    <div className={"min-h-[120px] w-full overflow-auto rounded-lg border p-3 " + className}>
      <pre>{sanitizedDiagramCode || "No diagram code provided."}</pre>
    </div>
  );
}

/**
 * OneQuestionAtATime Component
 * Displays worksheet questions one at a time with navigation
 */
const OneQuestionAtATime = ({
  worksheet,
  onAnswersChange,
  onUploadWork,
  onSubmitOnline,
  hintEnabled = true,
  reviewMode = false,
  previewMode = false,
  editable = false,
  overrideQuestions = null,
  onQuestionsChange = null,
  onDeleteQuestion = null,
  onSaveReview = null,
  initialAnswers = {},
  initialHints = {}
}) => {
  const { user } = useAuth();
  const showDifficulty = editable; // canUserSeeQuestionDifficulty(user);

  // State to track current question index (0-based)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // State to store all answers (keyed by question number)
  const [answers, setAnswers] = useState({});
  
  // State to track hint usage (keyed by question number)
  const [hintsUsed, setHintsUsed] = useState({});
  
  // State to track visible hints (keyed by question number)
  const [visibleHints, setVisibleHints] = useState({});
  
  // State for smooth transition animation
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hasHydratedRef = useRef(false);

  // Math keyboard for free-text answers (short answer / long answer / fill
  // in the blanks). Lazy-loaded on first toggle to keep MathLive out of the
  // main bundle; the <math-field> is uncontrolled, synced via a ref.
  const [isMathMode, setIsMathMode] = useState(false);
  const [isMathLiveLoaded, setIsMathLiveLoaded] = useState(false);
  const mathFieldRef = useRef(null);

  // Track which questions are currently being edited inline (preview mode only)
  const [inlineEditingQuestions, setInlineEditingQuestions] = useState(() => new Set());

  // Shared diagram editor workflow; applies the saved diagram to the question.
  const applyDiagram = useCallback((questionIndex, diagramObj) => {
    setAllQuestions((prevQuestions) => {
      const updated = [...prevQuestions];
      updated[questionIndex] = { ...updated[questionIndex], diagram: diagramObj };
      return updated;
    });
  }, []);
  const diagramTools = useDiagramEditor({ onApplyDiagram: applyDiagram });

  // Skip broadcasting allQuestions back to the parent whenever the change came
  // from a parent-provided overrideQuestions sync — otherwise the parent's
  // setEditableQuestions([]) cycle creates an oscillation that flashes
  // "No questions to preview".
  const skipNextBroadcastRef = useRef(false);

  // Extract questions from worksheet JSON. Initialize synchronously from
  // overrideQuestions so the first render already has questions when the
  // parent drives the list (review/preview modes).
  const [allQuestions, setAllQuestions] = useState(() => {
    if ((reviewMode || previewMode) && Array.isArray(overrideQuestions)) {
      skipNextBroadcastRef.current = true;
      return overrideQuestions;
    }
    return [];
  });

  // *** REMOVED ***
  // All global TikZJax state and loaders have been removed.
  // The DiagramRenderer component now handles its own loading.

  // Extract questions from worksheet JSON
  useEffect(() => {
    // Allow parent to drive questions directly in review or preview mode
    if ((reviewMode || previewMode) && Array.isArray(overrideQuestions)) {
      setAllQuestions((prev) => {
        if (prev === overrideQuestions) return prev;
        skipNextBroadcastRef.current = true;
        return overrideQuestions;
      });
      setCurrentQuestionIndex((prev) =>
        prev >= overrideQuestions.length ? Math.max(0, overrideQuestions.length - 1) : prev
      );
      return;
    }

    if (worksheet && worksheet.json_data) {
      try {
        const jsonData = typeof worksheet.json_data === 'string'
          ? JSON.parse(worksheet.json_data)
          : worksheet.json_data;
        
        // Extract all questions from the questions array with their question types
        const questions = [];
        if (jsonData.questions && Array.isArray(jsonData.questions)) {
          jsonData.questions.forEach(section => {
            if (section.questions_list && Array.isArray(section.questions_list)) {
              // Normalize the question type for easier rendering
              let sectionType = section.question_type || 'Multiple Choice';
              if (sectionType.includes('True') || sectionType.includes('False')) sectionType = 'True False';
              if (sectionType.includes('Assertion')) sectionType = 'Assertion Reasoning';

              const questionsWithType = section.questions_list.map((q, idx) => ({
                ...q,
                question_text: q.question_text || q.question || q.text || '',
                options: q.options || [],
                question_type: sectionType,
                question_number: q.question_number || questions.length + idx + 1,
                correct_answer: q.correct_answer || '',
                hint: q.hint || ''
              }));
              questions.push(...questionsWithType);
            }
          });
        }

        // Merge answer key into questions for review/edit flows
        if (jsonData.answer_key && Array.isArray(jsonData.answer_key)) {
          jsonData.answer_key.forEach(answer => {
            const questionIndex = questions.findIndex(q => q.question_number === answer.question_number);
            if (questionIndex !== -1) {
              questions[questionIndex].correct_answer = answer.answer_text || answer.answer || '';
            }
          });
        }

        setAllQuestions(questions);
      } catch (error) {
        console.error('Error parsing worksheet JSON:', error);
      }
    }
  }, [worksheet, reviewMode, previewMode, overrideQuestions]);

  useEffect(() => {
    if (!worksheet?.id) return;
    if (hasHydratedRef.current) return;
    const hasInitialAnswers = initialAnswers && Object.keys(initialAnswers).length > 0;
    const hasInitialHints = initialHints && Object.keys(initialHints).length > 0;
    if (hasInitialAnswers) {
      setAnswers(initialAnswers);
    }
    if (hasInitialHints) {
      setHintsUsed(initialHints);
    }
    if (hasInitialAnswers || hasInitialHints) {
      hasHydratedRef.current = true;
    }
  }, [worksheet?.id, initialAnswers, initialHints]);

  useEffect(() => {
    hasHydratedRef.current = false;
  }, [worksheet?.id]);

  useEffect(() => {
    if (!(reviewMode || previewMode) || !onQuestionsChange) return;
    if (skipNextBroadcastRef.current) {
      skipNextBroadcastRef.current = false;
      return;
    }
    onQuestionsChange(allQuestions);
  }, [allQuestions, reviewMode, previewMode, onQuestionsChange]);
  
  // Get current question (define before using in useEffect)
  const currentQuestion = allQuestions[currentQuestionIndex];
  const truncatedWorksheetTitle = useMemo(
    () => truncateTaskTitle(worksheet?.title || '', 30, { fallback: '', suffix: '…' }),
    [worksheet?.title],
  );
  const totalQuestions = allQuestions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const isFirstQuestion = currentQuestionIndex === 0;
  const displayedQuestionText = useMemo(() => {
    if (!currentQuestion) return '';
    const questionType = (currentQuestion.question_type || '').toLowerCase();
    const baseText = currentQuestion.question_text || currentQuestion.question || '';
    if (questionType.includes('assertion')) {
      return stripAssertionReasoningOptions(baseText);
    }
    return baseText;
  }, [currentQuestion]);

  const isQuestionAnswered = (question) => {
    if (!question) return false;

    const questionNumber = question.question_number;
    const answer = answers[questionNumber];
    const questionType = (question.question_type || '').toLowerCase();

    if (questionType.includes('true') && questionType.includes('false')) {
      return answer === 'True' || answer === 'False';
    }

    if (questionType.includes('assertion')) {
      return ASSERTION_REASONING_OPTIONS.includes(answer);
    }

    if (Array.isArray(question.options) && question.options.length > 0) {
      const normalizedOptions = question.options.map((option) => option.trim());
      return typeof answer === 'string' && normalizedOptions.includes(answer.trim());
    }

    return typeof answer === 'string' ? answer.trim().length > 0 : answer !== undefined && answer !== null;
  };
  
  // Close hint when moving to a different question
  useEffect(() => {
    if (currentQuestion) {
      const questionNumber = currentQuestion.question_number;
      // Close hint for the new question (it should start collapsed)
      setVisibleHints(prev => ({
        ...prev,
        [questionNumber]: false
      }));
    }
  }, [currentQuestionIndex, currentQuestion]);

  // Helpers for inline edits (review mode or preview-with-edit mode)
  const canEditQuestionFields = reviewMode || (previewMode && editable);

  const handleQuestionFieldChange = (field, value) => {
    if (!canEditQuestionFields) return;

    setAllQuestions(prevQuestions => {
      const updated = [...prevQuestions];
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        [field]: value
      };
      return updated;
    });
  };

  const handleOptionChange = (optionIndex, value) => {
    if (!canEditQuestionFields) return;

    setAllQuestions(prevQuestions => {
      const updated = [...prevQuestions];
      const options = [...(updated[currentQuestionIndex].options || [])];
      options[optionIndex] = value;
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        options
      };
      return updated;
    });
  };

  const handleAddOption = () => {
    if (!canEditQuestionFields) return;

    setAllQuestions(prevQuestions => {
      const updated = [...prevQuestions];
      const options = [...(updated[currentQuestionIndex].options || [])];
      options.push(`Option ${options.length + 1}`);
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        options
      };
      return updated;
    });
  };

  const handleRemoveOption = (optionIndex) => {
    if (!canEditQuestionFields) return;

    setAllQuestions(prevQuestions => {
      const updated = [...prevQuestions];
      const options = (updated[currentQuestionIndex].options || []).filter((_, idx) => idx !== optionIndex);
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        options
      };
      return updated;
    });
  };

  const handleDeleteCurrentQuestion = () => {
    if (!canEditQuestionFields) return;

    const removedQuestionNumber = allQuestions[currentQuestionIndex]?.question_number;

    setAllQuestions(prevQuestions => {
      const filtered = prevQuestions.filter((_, idx) => idx !== currentQuestionIndex)
        .map((q, idx) => ({ ...q, question_number: idx + 1 }));
      if (currentQuestionIndex >= filtered.length) {
        setCurrentQuestionIndex(Math.max(0, filtered.length - 1));
      }
      if (onDeleteQuestion) {
        onDeleteQuestion(filtered);
      }
      return filtered;
    });

    if (removedQuestionNumber !== undefined) {
      setInlineEditingQuestions(prev => {
        if (!prev.has(removedQuestionNumber)) return prev;
        const next = new Set(prev);
        next.delete(removedQuestionNumber);
        return next;
      });
    }
  };

  const toggleInlineEdit = (questionNumber) => {
    setInlineEditingQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionNumber)) {
        next.delete(questionNumber);
      } else {
        next.add(questionNumber);
      }
      return next;
    });
  };

  const renderMarksAndDiagramFields = () => (
    <div className="grid md:grid-cols-2 gap-4 md:gap-6">
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800">Marks</h4>
        <input
          type="number"
          min="1"
          max="10"
          value={currentQuestion.marks || 1}
          onChange={(e) => handleQuestionFieldChange('marks', parseInt(e.target.value, 10) || 1)}
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-400"
        />
      </div>
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800">Diagram</h4>
        <button
          type="button"
          onClick={() => diagramTools.openDiagramEditor(currentQuestion, currentQuestionIndex)}
          className={`px-4 py-2 text-sm rounded-lg border transition-colors duration-200 ${hasDiagram(currentQuestion)
            ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white border-transparent shadow-sm hover:from-indigo-600 hover:to-violet-600'
            : 'text-indigo-600 border-indigo-200 hover:border-indigo-300 bg-white hover:bg-indigo-50'}`}
        >
          {hasDiagram(currentQuestion) ? 'Edit Diagram' : 'Add Diagram'}
        </button>
      </div>
    </div>
  );

  const renderCorrectAnswerInput = () => {
    if (!currentQuestion) return null;

    const type = (currentQuestion.question_type || '').toLowerCase();
    const options = currentQuestion.options || [];

    // Radio selection for option based questions
    if (options.length > 0 || type.includes('true') || type.includes('assertion')) {
      // Assertion-Reasoning answers are always one of the four standard
      // A/R choices (the student view shows the same list), never True/False.
      let answerOptions = options;
      if (type.includes('assertion')) answerOptions = ASSERTION_REASONING_OPTIONS;
      else if (options.length === 0) answerOptions = ['True', 'False'];
      return (
        <div className="space-y-3">
          {answerOptions.map((option, idx) => {
            const optionValue = typeof option === 'string' ? option : option.text || `Option ${idx + 1}`;
            const isSelected = currentQuestion.correct_answer === optionValue;

            return (
              <label
                key={`${currentQuestion.question_number}-answer-${idx}`}
                className={`flex items-start p-3 border rounded-lg cursor-pointer ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
              >
                <input
                  type="radio"
                  name={`correct-${currentQuestion.question_number}`}
                  className="mt-1 mr-3"
                  checked={isSelected}
                  onChange={() => handleQuestionFieldChange('correct_answer', optionValue)}
                />
                <div className="text-gray-800">
                  <KaTeXContent content={optionValue} />
                </div>
              </label>
            );
          })}
        </div>
      );
    }

    // Free text answer for short/long/fill in the blanks
    return (
      <input
        type="text"
        value={currentQuestion.correct_answer || ''}
        onChange={(e) => handleQuestionFieldChange('correct_answer', e.target.value)}
        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
        placeholder="Enter the correct answer"
      />
    );
  };

  const renderReviewOptions = () => {
    if (!currentQuestion) return null;

    if (currentQuestion.question_type === 'Fill in the Blanks' || currentQuestion.question_type === 'Short Answer' || currentQuestion.question_type === 'Long Answer') {
      return null;
    }
    if ((currentQuestion.question_type || '').toLowerCase().includes('assertion')) {
      return <p className="text-sm text-gray-500">Assertion-Reasoning questions use the standard A/R choices.</p>;
    }

    const options = currentQuestion.options || [];

    return (
      <div className="space-y-3">
        {options.map((option, idx) => (
          <div key={`${currentQuestion.question_number}-option-${idx}`} className="flex items-start space-x-3">
            <input
              type="text"
              value={option}
              onChange={(e) => handleOptionChange(idx, e.target.value)}
              className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-400"
              placeholder={`Option ${idx + 1}`}
            />
            {options.length > 1 && (
              <button
                onClick={() => handleRemoveOption(idx)}
                className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              >
                Delete
              </button>
            )}
          </div>
        ))}
        <button
          onClick={handleAddOption}
          className="px-4 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
        >
          + Add Option
        </button>
      </div>
    );
  };

  const renderReviewMode = () => {
    if (!currentQuestion) {
      return (
        <div className="p-6 text-center text-gray-600 bg-white rounded-xl shadow">
          No questions available for review.
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold">
              Review Question {currentQuestion.question_number} / {totalQuestions}
            </div>
            <p className="mt-2 text-sm text-gray-600">Type: {currentQuestion.question_type || 'Multiple Choice'}</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleDeleteCurrentQuestion}
              className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
            >
              Delete
            </button>
            {onSaveReview && (
              <button
                onClick={() => onSaveReview(allQuestions)}
                className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Save Changes
              </button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Question Text</label>
            <textarea
              className="w-full min-h-[160px] p-3 border rounded-lg focus:ring-2 focus:ring-blue-400"
              value={currentQuestion.question_text || currentQuestion.question || ''}
              onChange={(e) => handleQuestionFieldChange('question_text', e.target.value)}
              placeholder="Enter the question prompt"
            />
          </div>
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Live Preview</label>
            <div className="p-3 border rounded-lg bg-gray-50">
              {/* Skip diagram while editing — a second iframe alongside the
                  "Edit Diagram" dialog destabilises the modal. Use the Edit
                  Diagram button below to view/update the diagram. */}
              <QuestionContent
                questionNumber={currentQuestion.question_number}
                questionText={currentQuestion.question_text || currentQuestion.question || ''}
                marks={currentQuestion.marks}
                diagram={null}
                DiagramRenderer={DiagramRenderer}
                worksheetId={worksheet?.id}
                difficulty={currentQuestion.difficulty_level || currentQuestion.difficulty}
                showDifficulty={showDifficulty}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Options</h4>
              <span className="text-xs text-gray-500">Edit or reorder as needed</span>
            </div>
            {renderReviewOptions()}
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-800">Correct Answer</h4>
            {renderCorrectAnswerInput()}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-800">Hint (optional)</h4>
          <textarea
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-400"
            value={currentQuestion.hint || ''}
            onChange={(e) => handleQuestionFieldChange('hint', e.target.value)}
            placeholder="Provide a hint to help students if needed"
          />
        </div>

        {renderMarksAndDiagramFields()}

        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={isFirstQuestion}
            className={`px-6 py-2 rounded-lg text-sm font-medium sm:px-4 sm:py-2 ${isFirstQuestion ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
          >
            ← Previous
          </button>

          <div className="flex space-x-3">
            <div className="text-sm text-gray-500 self-center">Use navigation to review every question</div>
            <button
              onClick={handleNext}
              disabled={isLastQuestion}
              className={`px-6 py-2 rounded-lg text-sm font-medium sm:px-4 sm:py-2  ${isLastQuestion ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderInlineEditFields = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Question Text</label>
        <textarea
          className="w-full min-h-[120px] p-3 border rounded-lg focus:ring-2 focus:ring-blue-400"
          value={currentQuestion.question_text || currentQuestion.question || ''}
          onChange={(e) => handleQuestionFieldChange('question_text', e.target.value)}
          placeholder="Enter the question prompt"
        />
        {(currentQuestion.question_text || currentQuestion.question) && (
          <div className="p-3 border rounded-lg bg-gray-50">
            <p className="mb-1 text-xs font-semibold text-gray-600">Live Preview</p>
            {/* Skip diagram while editing — a second iframe alongside the
                "Edit Diagram" dialog destabilises the modal. Use the Edit
                Diagram button below to view/update the diagram. */}
            <QuestionContent
              questionNumber={currentQuestion.question_number}
              questionText={currentQuestion.question_text || currentQuestion.question || ''}
              marks={currentQuestion.marks}
              diagram={null}
              DiagramRenderer={DiagramRenderer}
              worksheetId={worksheet?.id}
              difficulty={currentQuestion.difficulty_level || currentQuestion.difficulty}
              showDifficulty={showDifficulty}
            />
          </div>
        )}
      </div>

      {(() => {
        const type = (currentQuestion.question_type || '').toLowerCase();
        const isShortLongFill =
          type.includes('short') || type.includes('long') || type.includes('fill');
        // Assertion-Reasoning uses the fixed A/R choices shown under Correct Answer.
        if (isShortLongFill || type.includes('assertion')) return null;

        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Options</h4>
              <span className="text-xs text-gray-500">Edit or reorder as needed</span>
            </div>
            {renderReviewOptions()}
          </div>
        );
      })()}

      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800">Correct Answer</h4>
        {renderCorrectAnswerInput()}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800">Hint (optional)</h4>
        <textarea
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-400"
          value={currentQuestion.hint || ''}
          onChange={(e) => handleQuestionFieldChange('hint', e.target.value)}
          placeholder="Provide a hint to help students if needed"
        />
      </div>

      {renderMarksAndDiagramFields()}
    </div>
  );

  const renderPreviewReadOnly = () => {
    const type = (currentQuestion.question_type || '').toLowerCase();
    const isTrueFalse = type.includes('true') && type.includes('false');
    const isAssertionReasoning = type.includes('assertion');

    let displayOptions = [];
    if (isTrueFalse) {
      displayOptions = ['True', 'False'];
    } else if (isAssertionReasoning) {
      displayOptions = ASSERTION_REASONING_OPTIONS;
    } else if (Array.isArray(currentQuestion.options)) {
      displayOptions = currentQuestion.options;
    }

    const correctAnswer = currentQuestion.correct_answer || '';
    const normalizedCorrect = typeof correctAnswer === 'string' ? correctAnswer.trim() : '';
    const hasOptions = displayOptions.length > 0;

    return (
      <div className="space-y-5">
        <QuestionContent
          questionNumber={currentQuestion.question_number}
          questionText={displayedQuestionText}
          marks={currentQuestion.marks}
          diagram={currentQuestion.diagram}
          DiagramRenderer={DiagramRenderer}
          worksheetId={worksheet?.id}
          difficulty={currentQuestion.difficulty_level || currentQuestion.difficulty}
          showDifficulty={showDifficulty}
        />

        {hasOptions ? (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Options</h4>
            {displayOptions.map((option, idx) => {
              const optionValue = typeof option === 'string' ? option : option.text || `Option ${idx + 1}`;
              const isCorrect =
                normalizedCorrect.length > 0 &&
                optionValue.trim() === normalizedCorrect;
              return (
                <div
                  key={`preview-${currentQuestion.question_number}-${idx}`}
                  className={`flex items-start p-3 border-2 rounded-lg ${
                    isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="text-gray-800 flex-1">
                    <KaTeXContent content={optionValue} />
                  </div>
                  {isCorrect && (
                    <span className="ml-3 text-green-700 font-semibold text-sm whitespace-nowrap">
                      ✓ Correct
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          normalizedCorrect.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Correct Answer</h4>
              <div className="p-3 border-2 border-green-500 bg-green-50 rounded-lg text-gray-800">
                <KaTeXContent content={correctAnswer} />
              </div>
            </div>
          )
        )}

        {hintEnabled && currentQuestion.hint && currentQuestion.hint.trim() && (
          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💡</span>
              <div>
                <p className="text-sm font-semibold text-yellow-800 mb-1">Hint</p>
                <KaTeXContent content={currentQuestion.hint} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPreviewMode = () => {
    if (!currentQuestion) {
      return (
        <div className="p-6 text-center text-gray-600 bg-white rounded-xl shadow">
          No questions available.
        </div>
      );
    }

    const isEditingThis =
      editable && inlineEditingQuestions.has(currentQuestion.question_number);

    return (
      <div className="max-w-3xl mx-auto">
        {truncatedWorksheetTitle && (
          <div className="mb-3 text-sm font-medium text-gray-700" title={worksheet?.title || ''}>
            {truncatedWorksheetTitle}
          </div>
        )}

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}% Reviewed
            </span>
          </div>
          <div
            className="relative w-full bg-gray-200 rounded-full h-2 cursor-pointer touch-manipulation"
            onClick={handleProgressClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                handleJumpTo(currentQuestionIndex - 1);
              }
              if (event.key === 'ArrowRight') {
                handleJumpTo(currentQuestionIndex + 1);
              }
            }}
            aria-label="Jump to a question"
            title="Tap or click to jump to a question"
          >
            <div className="absolute inset-0 flex h-2 overflow-hidden rounded-full bg-gray-200">
              {allQuestions.map((question, index) => {
                const isCurrent = index === currentQuestionIndex;
                return (
                  <div
                    key={`preview-progress-${question.question_number}-${index}`}
                    className={`h-full flex-1 transition-colors duration-200 ${
                      index <= currentQuestionIndex ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isCurrent ? 'ring-2 ring-inset ring-white/90' : ''}`}
                  />
                );
              })}
            </div>
            <input
              type="range"
              min={1}
              max={totalQuestions}
              value={currentQuestionIndex + 1}
              onChange={handleProgressChange}
              className="absolute inset-0 h-2 w-full opacity-0 cursor-pointer"
              aria-label="Question progress"
            />
          </div>
        </div>

        <div
          className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 transition-all duration-150 ${
            isTransitioning ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              {/*<div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">
                Question {currentQuestion.question_number} of {totalQuestions}
              </div>*/}
              <p className="mt-2 text-sm text-gray-600">
                Type: {currentQuestion.question_type || 'Multiple Choice'}
              </p>
            </div>
            {editable && (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => toggleInlineEdit(currentQuestion.question_number)}
                  className={`px-3 py-2 text-xs sm:text-sm rounded-lg border ${
                    isEditingThis
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  {isEditingThis ? 'Done Editing' : '✏ Edit'}
                </button>
                {isEditingThis && (
                  <button
                    type="button"
                    onClick={handleDeleteCurrentQuestion}
                    className="px-3 py-2 text-xs sm:text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>

          {isEditingThis ? renderInlineEditFields() : renderPreviewReadOnly()}
        </div>

        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={isFirstQuestion}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center ${
              isFirstQuestion
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <span className="mr-2">←</span>
            Previous
          </button>

          <button
            onClick={handleNext}
            disabled={isLastQuestion}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center ${
              isLastQuestion
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <span className="ml-2">→</span>
          </button>
        </div>
      </div>
    );
  };

  // Handle answer change for current question
  const handleAnswerChange = (value) => {
    const questionNumber = currentQuestion.question_number;
    const newAnswers = {
      ...answers,
      [questionNumber]: value
    };
    setAnswers(newAnswers);
    
    // Notify parent component with answers and hint usage
    if (onAnswersChange) {
      onAnswersChange(newAnswers, hintsUsed);
    }
  };
  
  // Lazy-load MathLive (registers the <math-field> custom element) only when
  // the math keyboard is first toggled on, to keep it out of the main bundle.
  useEffect(() => {
    if (!isMathMode || isMathLiveLoaded) return;
    import('mathlive').then(() => setIsMathLiveLoaded(true));
  }, [isMathMode, isMathLiveLoaded]);

  // <math-field> is an uncontrolled custom element; sync its value into the
  // current question's answer via a native 'input' listener instead of a
  // React value prop.
  useEffect(() => {
    const field = mathFieldRef.current;
    if (!isMathMode || !isMathLiveLoaded || !field) return;
    field.value = answers[currentQuestion?.question_number] || '';
    const handleMathInput = (e) => handleAnswerChange(e.target.value);
    field.addEventListener('input', handleMathInput);
    return () => field.removeEventListener('input', handleMathInput);
  }, [isMathMode, isMathLiveLoaded, currentQuestion?.question_number]);

  const toggleMathMode = () => setIsMathMode(prev => !prev);

  // Handle hint toggle
  const handleHintToggle = () => {
    const questionNumber = currentQuestion.question_number;
    
    // Mark hint as used (only mark on first click)
    if (!hintsUsed[questionNumber]) {
      const newHintsUsed = {
        ...hintsUsed,
        [questionNumber]: true
      };
      setHintsUsed(newHintsUsed);
      
      // Notify parent component with updated hint usage
      if (onAnswersChange) {
        onAnswersChange(answers, newHintsUsed);
      }
    }
    
    // Toggle hint visibility
    setVisibleHints(prev => ({
      ...prev,
      [questionNumber]: !prev[questionNumber]
    }));
  };

  // Navigate to next question with animation
  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setIsTransitioning(false);
      }, 150); // Short animation duration
    }
  };

  // Navigate to previous question with animation
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setIsTransitioning(false);
      }, 150);
    }
  };

  const handleJumpTo = (targetIndex) => {
    if (targetIndex === currentQuestionIndex) return;
    if (targetIndex < 0 || targetIndex >= totalQuestions) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentQuestionIndex(targetIndex);
      setIsTransitioning(false);
    }, 150);
  };

  const handleProgressClick = (event) => {
    if (!totalQuestions) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, offsetX / rect.width));
    const targetIndex = Math.round(ratio * (totalQuestions - 1));
    handleJumpTo(targetIndex);
  };

  const handleProgressChange = (event) => {
    const value = Number(event.target.value);
    if (!Number.isNaN(value)) {
      handleJumpTo(value - 1);
    }
  };

  // Render question options (for MCQ)
  const renderOptions = () => {
    if (!currentQuestion.options || !Array.isArray(currentQuestion.options)) {
      return null;
    }

    const questionNumber = currentQuestion.question_number;
    const currentAnswer = answers[questionNumber] || '';

    return (
      <div className="space-y-3 mt-4">
        {currentQuestion.options.map((option, idx) => {
          const optionValue = option.trim();
          const isSelected = currentAnswer === optionValue;
          
          return (
            <label
              key={`${questionNumber}-${idx}`}
              className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name={`question-${questionNumber}`}
                value={optionValue}
                checked={isSelected}
                onChange={(e) => handleAnswerChange(e.target.value)}
                className="mt-1 mr-3"
              />
              <div className="text-gray-800 flex-1">
                <KaTeXContent content={optionValue} />
              </div>
            </label>
          );
        })}
      </div>
    );
  };

  // Render radio buttons for True/False questions
  const renderTrueFalse = () => {
    const questionNumber = currentQuestion.question_number;
    const currentAnswer = answers[questionNumber] || '';
    const options = ['True', 'False'];

    return (
      <div className="space-y-3 mt-4">
        {options.map((option, idx) => {
          const isSelected = currentAnswer === option;
          
          return (
            <label
              key={`${questionNumber}-tf-${idx}`}
              className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name={`question-${questionNumber}`}
                value={option}
                checked={isSelected}
                onChange={(e) => handleAnswerChange(e.target.value)}
                className="mt-1 mr-3"
              />
              <span className="text-gray-800">{option}</span>
            </label>
          );
        })}
      </div>
    );
  };

  // Render radio buttons for Assertion-Reasoning questions
  const renderAssertionReasoning = () => {
    const questionNumber = currentQuestion.question_number;
    const currentAnswer = answers[questionNumber] || '';
    const options = [
      'Both A and R are true, and R is the correct explanation of A',
      'Both A and R are true, but R is NOT the correct explanation of A',
      'A is true, but R is false',
      'A is false, but R is true',
    ];

    return (
      <div className="space-y-3 mt-4">
        {options.map((option, idx) => {
          const isSelected = currentAnswer === option;
          const optionLabel = String.fromCharCode(97 + idx); // a, b, c, d, e
          
          return (
            <label
              key={`${questionNumber}-ar-${idx}`}
              className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name={`question-${questionNumber}`}
                value={option}
                checked={isSelected}
                onChange={(e) => handleAnswerChange(e.target.value)}
                className="mt-1 mr-3"
              />
              <span className="text-gray-800">
                {option}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  // Render text input (for fill in the blanks, short answer, etc.)
  const renderTextInput = () => {
    const questionNumber = currentQuestion.question_number;
    const currentAnswer = answers[questionNumber] || '';

    return (
      <div className="mt-4">
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={toggleMathMode}
            title={isMathMode ? 'Switch to plain text' : 'Enter a math equation'}
            aria-label="Toggle math keyboard"
            aria-pressed={isMathMode}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full border text-sm italic transition-colors ${
              isMathMode
                ? 'bg-blue-600 text-white border-transparent'
                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-blue-600'
            }`}
          >
            Σ
          </button>
        </div>
        {isMathMode ? (
          isMathLiveLoaded ? (
            // eslint-disable-next-line react/no-unknown-property
            <math-field
              ref={mathFieldRef}
              class="math-field-answer-input"
              math-virtual-keyboard-policy="auto"
            />
          ) : (
            <div className="w-full p-4 border-2 border-gray-200 rounded-lg text-gray-500 text-sm">
              Loading math keyboard…
            </div>
          )
        ) : (
          <textarea
            value={currentAnswer}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Type your answer here..."
            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
            rows={4}
          />
        )}
      </div>
    );
  };

  if (reviewMode) {
    return (
      <>
        {renderReviewMode()}
        <DiagramEditorHost {...diagramTools} />
      </>
    );
  }

  if (previewMode) {
    if (allQuestions.length === 0 || !currentQuestion) {
      return (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">📝</div>
          <p className="text-sm text-gray-600">No questions to preview.</p>
        </div>
      );
    }
    return (
      <>
        {renderPreviewMode()}
        <DiagramEditorHost {...diagramTools} />
      </>
    );
  }

  if (allQuestions.length === 0 || !currentQuestion) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg">
        <div className="text-4xl mb-4">📝</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {truncatedWorksheetTitle && (
        <div className="mb-3 text-sm font-medium text-gray-700" title={worksheet?.title || ''}>
          {truncatedWorksheetTitle}
        </div>
      )}
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}% Complete
          </span>
        </div>
        {/* Progress Bar */}
        <div
          className="relative w-full bg-gray-200 rounded-full h-2 cursor-pointer touch-manipulation"
          onClick={handleProgressClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') {
              handleJumpTo(currentQuestionIndex - 1);
            }
            if (event.key === 'ArrowRight') {
              handleJumpTo(currentQuestionIndex + 1);
            }
          }}
          aria-label="Jump to a question"
          title="Tap or click to jump to a question"
        >
          <div className="absolute inset-0 flex h-2 overflow-hidden rounded-full bg-gray-200">
            {allQuestions.map((question, index) => {
              const isAnswered = isQuestionAnswered(question);
              const isCurrent = index === currentQuestionIndex;

              return (
                <div
                  key={`progress-${question.question_number}-${index}`}
                  className={`h-full flex-1 transition-colors duration-200 ${
                    isAnswered ? 'bg-blue-600' : 'bg-amber-500'
                  } ${isCurrent ? 'ring-2 ring-inset ring-white/90' : ''}`}
                />
              );
            })}
          </div>
          <input
            type="range"
            min={1}
            max={totalQuestions}
            value={currentQuestionIndex + 1}
            onChange={handleProgressChange}
            className="absolute inset-0 h-2 w-full opacity-0 cursor-pointer"
            aria-label="Question progress"
          />
        </div>
      </div>

      {/* Question Card with Transition Animation */}
      <div
        className={`one-question-display bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg transition-all duration-150 ${
          isTransitioning ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
        }`}
      >
        <QuestionContent
          questionNumber={currentQuestion.question_number}
          questionText={displayedQuestionText}
          marks={currentQuestion.marks}
          diagram={currentQuestion.diagram}
          DiagramRenderer={DiagramRenderer}
          worksheetId={worksheet?.id}
          difficulty={currentQuestion.difficulty_level || currentQuestion.difficulty}
          showDifficulty={showDifficulty}
        />

        {/* Answer Input Area */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Answer:</h4>
          {/* Check question type and render appropriate input */}
          {(() => {
            const questionType = (currentQuestion.question_type || '').toLowerCase();
            const isTrueFalse = questionType.includes('true') && questionType.includes('false');
            const isAssertionReasoning = questionType.includes('assertion');

            if (isTrueFalse) {
              return renderTrueFalse();
            }

            if (isAssertionReasoning) {
              return renderAssertionReasoning();
            }

            if (currentQuestion.options && currentQuestion.options.length > 0) {
              return renderOptions();
            }

            return renderTextInput();
          })()}
        </div>
        
        {/* Hint Section */}
        {hintEnabled && currentQuestion.hint && currentQuestion.hint.trim() && (
          <div className="mt-4">
            <button
              onClick={handleHintToggle}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                hintsUsed[currentQuestion.question_number]
                  ? 'bg-orange-100 text-orange-700 border-2 border-orange-300'
                  : 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-200'
              }`}
            >
              <span className="text-lg">💡</span>
              <span>
                {hintsUsed[currentQuestion.question_number] ? 'Hint Used' : 'Need a Hint?'}
                {hintsUsed[currentQuestion.question_number] && ' (marks will be scaled)'}
              </span>
              <span className="text-sm">
                {visibleHints[currentQuestion.question_number] ? '▼' : '▶'}
              </span>
            </button>
            
            {visibleHints[currentQuestion.question_number] && (
              <div className="mt-3 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-sm font-semibold text-yellow-800 mb-1">Hint:</p>
                    <p className="text-sm text-yellow-900">{currentQuestion.hint}</p>
                    {hintsUsed[currentQuestion.question_number] && (
                      <p className="text-xs text-orange-600 mt-2 italic">
                        Note: Marks for this question will be scaled by hint factor during evaluation.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 flex justify-between items-center">
        {/* Back Button */}
        <button
          onClick={handleBack}
          disabled={isFirstQuestion}
          className={`px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-all flex items-center ${
            isFirstQuestion
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          <span className="mr-2">←</span>
          Back
        </button>

        {/* Next or Submit Buttons */}
        {!isLastQuestion ? (
          <div className="flex space-x-3">
            <button
              onClick={onUploadWork}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-sm sm:text-base hover:bg-red-700 transition-all flex items-center"
            >
              <span className="mr-2">📤</span>
              Upload
            </button>
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm sm:text-base hover:bg-blue-700 transition-all flex items-center"
            >
              Next
              <span className="ml-2">→</span>
            </button>
          </div>
        ) : (
          <div className="flex space-x-3">
            <button
              onClick={onUploadWork}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-sm sm:text-base hover:bg-red-700 transition-all flex items-center"
            >
              <span className="mr-2">📤</span>
              Upload
            </button>
            <button
              onClick={onSubmitOnline}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm sm:text-base hover:bg-green-700 transition-all flex items-center"
            >
              <span className="mr-2">📋</span>
              Submit
            </button>
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <div className="text-blue-600 mr-3 mt-0.5">💡</div>
          <div>
            <p className="text-sm text-blue-800">
              {isLastQuestion 
                ? "This is the last question! Review your answer and choose how to submit your work."
                : "Answer this question, then click 'Next' to continue. Your answers are saved automatically."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneQuestionAtATime;